import { appendValidatedParam } from '@/util/append-param'
import { get, post } from '@/util/auth'

import {
  API_ROUTE_ORDERS_PRODUCTS,
  API_ROUTE_ORDERS_RELATED_PRODUCTS,
  API_ROUTE_ORDERS_PRODUCTS_CATEGORY_CLASSIFICATION,
} from './apiRoutes'

const IS_NEW_BACKEND_API = true

export type ProductSort = { sortBy?: string; sort?: 'desc' | 'asc' }

type ClassificationsOriginal = {
  classifications: Array<string>
  specificClassifications: Record<string, string>
}

type ProductOriginal = {
  id: number
  category: string | null
  articleName: string
  productName: string | null
  imageUrl: string | null
  price: number
  currency: string
  manufacturerProductNo: string
  articleNo: string
  vendorUniqueId: string | null
}
type RelatedProductOriginal = ProductOriginal & {
  selected?: boolean
}

type PaginationOriginal = {
  currentPage: number
  perPage: number
  totalPages: number
  totalCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

type ProductWithPaginationOriginal<T> = PaginationOriginal & {
  products: Array<T>
}

type RelatedProductWithPaginationOriginal<T> = PaginationOriginal & {
  products: Array<T>
  relatedCategories?: Array<string>
  numberOfRelatedProducts?: number
}

export const productsResources = {
  getClassifications: async () => {
    const response = await get<ClassificationsOriginal>(
      API_ROUTE_ORDERS_PRODUCTS_CATEGORY_CLASSIFICATION,
      IS_NEW_BACKEND_API
    )
    const { classifications, specificClassifications } = response
    const extendedClassifications = ['ALL', ...classifications]

    return {
      classifications: extendedClassifications,
      specificClassifications,
    }
  },
  getProducts: async ({
    pagination: { page, limit },
    columnFilters: { keyword, category },
    sorting: { sortBy, sort },
  }: {
    pagination: {
      page: number
      limit: number
    }
    columnFilters: {
      keyword?: string
      category?: string
    }
    sorting: ProductSort
  }) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    appendValidatedParam({
      params,
      paramName: 'keyword',
      value: keyword,
    })
    appendValidatedParam({
      params,
      paramName: 'category',
      value: category,
    })
    if (sortBy && sort) {
      appendValidatedParam({
        params,
        paramName: 'sortBy',
        value: sortBy,
      })
      appendValidatedParam({
        params,
        paramName: 'sort',
        value: sort,
      })
    }
    const response = await get<ProductWithPaginationOriginal<ProductOriginal>>(
      `${API_ROUTE_ORDERS_PRODUCTS}?${params.toString()}`,
      IS_NEW_BACKEND_API
    )
    const { products, ...pagination } = response
    return {
      items: products,
      pagination,
    }
  },
  getRelatedProducts: async ({
    productId,
    pagination: { page, limit },
    columnFilters: { keyword, category },
    sorting: { sortBy, sort },
    selectedFirst,
  }: {
    productId: number
    pagination: {
      page: number
      limit: number
    }
    columnFilters: {
      keyword?: string
      category?: string[]
    }
    sorting: ProductSort
    selectedFirst?: boolean
  }) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    appendValidatedParam({
      params,
      paramName: 'keyword',
      value: keyword,
    })

    if (category) {
      appendValidatedParam({
        params,
        paramName: 'category',
        value: category.join(', '),
      })
    }

    if (sortBy && sort) {
      appendValidatedParam({
        params,
        paramName: 'sortBy',
        value: sortBy,
      })
      appendValidatedParam({
        params,
        paramName: 'sort',
        value: sort,
      })
    }
    if (selectedFirst) {
      appendValidatedParam({
        params,
        paramName: 'selectedFirst',
        value: String(selectedFirst),
      })
    }
    const response = await get<RelatedProductWithPaginationOriginal<RelatedProductOriginal>>(
      `${API_ROUTE_ORDERS_RELATED_PRODUCTS.replace(':productId', String(productId))}?${params.toString()}`,
      IS_NEW_BACKEND_API
    )
    const { products, relatedCategories, numberOfRelatedProducts, ...pagination } = response
    return {
      items: products,
      relatedCategories,
      numberOfRelatedProducts,
      pagination,
    }
  },

  // Mutations
  updateRelatedProducts: ({
    productId,
    data,
  }: {
    productId: number
    data: { addProducts: number[]; removeProducts: number[] }
  }) =>
    post(
      API_ROUTE_ORDERS_RELATED_PRODUCTS.replace(':productId', String(productId)),
      data,
      IS_NEW_BACKEND_API
    ),
}

export type Classifications = Awaited<
  ReturnType<typeof productsResources.getClassifications>
>['classifications'][number]
export type SpecificClassifications = Awaited<
  ReturnType<typeof productsResources.getClassifications>
>['specificClassifications']

export type Product = Awaited<ReturnType<typeof productsResources.getProducts>>['items'][number]

export type RelatedProductResponse = Awaited<
  ReturnType<typeof productsResources.getRelatedProducts>
>
export type RelatedProduct = Awaited<
  ReturnType<typeof productsResources.getRelatedProducts>
>['items'][number]
export type RelatedCategories = Awaited<
  ReturnType<typeof productsResources.getRelatedProducts>
>['relatedCategories']
export type RelatedPagination = Awaited<
  ReturnType<typeof productsResources.getRelatedProducts>
>['pagination']
